import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Table, Button, Tag, Space, Modal, message, Input, Select, DatePicker, Tooltip, Card, Row, Col, Statistic } from 'antd';
import { 
    PlusOutlined, 
    EditOutlined, 
    DeleteOutlined, 
    EyeOutlined, 
    PoweroffOutlined,
    SearchOutlined,
    DownloadOutlined,
    PercentageOutlined,
    DollarOutlined
} from '@ant-design/icons';
import { Coupon, CouponStatus, CouponType } from '../../../../types/coupon.types';
import couponService from '../../../../services/coupon/coupon.service';
import { all_routes } from '../../../../router/all_routes';
import dayjs from 'dayjs';

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const CouponsPage: React.FC = () => {
    const [coupons, setCoupons] = useState<Coupon[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState<CouponStatus | 'all'>('all');
    const [typeFilter, setTypeFilter] = useState<CouponType | 'all'>('all');
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
    const [globalStats, setGlobalStats] = useState<any>(null);

    useEffect(() => {
        fetchCoupons();
    }, []);

    const fetchCoupons = async () => {
        try {
            setLoading(true);
            const data = await couponService.getAllCoupons();
            setCoupons(data);
            
            // Global istatistikleri hesapla
            const totalDiscountGiven = data.reduce((sum, coupon) => {
                if (coupon.usageHistory) {
                    return sum + coupon.usageHistory.reduce((historySum, usage) => historySum + usage.discountAmount, 0);
                }
                return sum;
            }, 0);
            
            setGlobalStats({
                totalDiscountGiven,
                totalUsage: data.reduce((sum, c) => sum + c.usedCount, 0),
                averageDiscount: totalDiscountGiven / Math.max(data.reduce((sum, c) => sum + c.usedCount, 0), 1)
            });
        } catch (error) {
            message.error('Kuponlar yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleStatus = async (id: number) => {
        try {
            await couponService.toggleCouponStatus(id);
            message.success('Kupon durumu güncellendi');
            fetchCoupons();
        } catch (error) {
            message.error('Kupon durumu güncellenirken hata oluştu');
        }
    };

    const handleDelete = async (id: number) => {
        Modal.confirm({
            title: 'Kupon Silme',
            content: 'Bu kuponu silmek istediğinizden emin misiniz?',
            okText: 'Evet, Sil',
            cancelText: 'İptal',
            okType: 'danger',
            onOk: async () => {
                try {
                    await couponService.deleteCoupon(id);
                    message.success('Kupon başarıyla silindi');
                    fetchCoupons();
                } catch (error) {
                    message.error('Kupon silinirken hata oluştu');
                }
            }
        });
    };

    const getStatusColor = (status: CouponStatus) => {
        switch (status) {
            case CouponStatus.ACTIVE:
                return 'success';
            case CouponStatus.INACTIVE:
                return 'default';
            case CouponStatus.EXPIRED:
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusText = (status: CouponStatus) => {
        switch (status) {
            case CouponStatus.ACTIVE:
                return 'Aktif';
            case CouponStatus.INACTIVE:
                return 'Pasif';
            case CouponStatus.EXPIRED:
                return 'Süresi Dolmuş';
            default:
                return status;
        }
    };

    const getTypeIcon = (type: CouponType) => {
        return type === CouponType.PERCENTAGE ? <PercentageOutlined /> : <DollarOutlined />;
    };

    const getTypeText = (type: CouponType) => {
        return type === CouponType.PERCENTAGE ? 'Yüzde' : 'Sabit Tutar';
    };

    const filteredCoupons = coupons.filter(coupon => {
        const matchesSearch = coupon.code.toLowerCase().includes(searchText.toLowerCase()) ||
                            coupon.name.toLowerCase().includes(searchText.toLowerCase());
        const matchesStatus = statusFilter === 'all' || coupon.status === statusFilter;
        const matchesType = typeFilter === 'all' || coupon.type === typeFilter;
        
        let matchesDate = true;
        if (dateRange) {
            const couponStart = dayjs(coupon.startDate);
            const couponEnd = dayjs(coupon.endDate);
            matchesDate = couponStart.isBetween(dateRange[0], dateRange[1], null, '[]') ||
                         couponEnd.isBetween(dateRange[0], dateRange[1], null, '[]');
        }

        return matchesSearch && matchesStatus && matchesType && matchesDate;
    });

    const columns = [
        {
            title: 'Kupon Kodu',
            dataIndex: 'code',
            key: 'code',
            render: (code: string, record: Coupon) => (
                <div className="d-flex align-items-center">
                    <div className="me-2 text-primary fs-5">
                        {getTypeIcon(record.type)}
                    </div>
                    <div>
                        <div className="fw-bold text-primary">{code}</div>
                        <small className="text-muted">{record.name}</small>
                    </div>
                </div>
            ),
        },
        {
            title: 'İndirim',
            dataIndex: 'value',
            key: 'value',
            render: (value: number, record: Coupon) => (
                <div style={{ textAlign: 'center' }}>
                    <span style={{ fontWeight: 500, color: '#28a745' }}>
                        {record.type === CouponType.PERCENTAGE ? `%${value}` : `₺${value}`}
                    </span>
                    <span style={{ marginLeft: '8px', color: '#6c757d' }}>
                        ({getTypeText(record.type)})
                    </span>
                </div>
            ),
        },
        {
            title: 'Kullanım',
            key: 'usage',
            render: (record: Coupon) => (
                <div className="text-center">
                    <div className="fw-bold">
                        {record.usedCount} / {record.usageLimit === 0 ? '∞' : record.usageLimit}
                    </div>
                    <small className="text-muted">Kullanım</small>
                </div>
            ),
        },
        {
            title: 'Geçerlilik',
            key: 'validity',
            render: (record: Coupon) => (
                <div className="text-center">
                    <div className="small">
                        <div>{dayjs(record.startDate).format('DD.MM.YYYY')}</div>
                        <div className="text-muted">-</div>
                        <div>{dayjs(record.endDate).format('DD.MM.YYYY')}</div>
                    </div>
                </div>
            ),
        },
        {
            title: 'Durum',
            dataIndex: 'status',
            key: 'status',
            render: (status: CouponStatus) => (
                <Tag color={getStatusColor(status)} className="px-2 py-1">
                    {getStatusText(status)}
                </Tag>
            ),
        },
        {
            title: 'İşlemler',
            key: 'actions',
            render: (record: Coupon) => (
                <Space>
                    <Tooltip title="Detayları Görüntüle">
                        <Link to={`${all_routes.coupons}/${record.id}`}>
                            <Button 
                                type="text" 
                                icon={<EyeOutlined />} 
                                size="small"
                                className="text-primary"
                            />
                        </Link>
                    </Tooltip>
                    <Tooltip title="Düzenle">
                        <Link to={`${all_routes.coupons}/${record.id}/edit`}>
                            <Button 
                                type="text" 
                                icon={<EditOutlined />} 
                                size="small"
                                className="text-warning"
                            />
                        </Link>
                    </Tooltip>
                    <Tooltip title={record.status === CouponStatus.ACTIVE ? 'Pasif Yap' : 'Aktif Yap'}>
                        <Button 
                            type="text" 
                            icon={<PoweroffOutlined />} 
                            size="small"
                            className={record.status === CouponStatus.ACTIVE ? 'text-danger' : 'text-success'}
                            onClick={() => handleToggleStatus(record.id)}
                        />
                    </Tooltip>
                    <Tooltip title="Sil">
                        <Button 
                            type="text" 
                            icon={<DeleteOutlined />} 
                            size="small"
                            className="text-danger"
                            onClick={() => handleDelete(record.id)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const stats = {
        total: coupons.length,
        active: coupons.filter(c => c.status === CouponStatus.ACTIVE).length,
        expired: coupons.filter(c => c.status === CouponStatus.EXPIRED).length,
        totalUsage: coupons.reduce((sum, c) => sum + c.usedCount, 0),
    };

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <div>
                        <h4 className="page-title mb-1">
                            <i className="ti ti-ticket me-2 text-primary"></i>
                            Kupon Yönetimi
                        </h4>
                        <p className="text-muted mb-0">Tüm kuponları yönetin ve takip edin</p>
                    </div>
                    <Link to={all_routes.createCoupon} className="btn btn-primary">
                        <PlusOutlined className="me-2" />
                        Yeni Kupon Oluştur
                    </Link>
                </div>

                {/* İstatistikler */}
                <Row gutter={16} className="mb-4">
                    <Col xs={12} sm={6}>
                        <Card className="text-center border-0 shadow-sm">
                            <Statistic
                                title="Toplam Kupon"
                                value={stats.total}
                                prefix={<i className="ti ti-ticket text-primary"></i>}
                                valueStyle={{ color: '#1890ff', fontSize: '24px' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card className="text-center border-0 shadow-sm">
                            <Statistic
                                title="Aktif Kupon"
                                value={stats.active}
                                prefix={<i className="ti ti-check-circle text-success"></i>}
                                valueStyle={{ color: '#52c41a', fontSize: '24px' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card className="text-center border-0 shadow-sm">
                            <Statistic
                                title="Süresi Dolan"
                                value={stats.expired}
                                prefix={<i className="ti ti-clock-x text-danger"></i>}
                                valueStyle={{ color: '#ff4d4f', fontSize: '24px' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card className="text-center border-0 shadow-sm">
                            <Statistic
                                title="Toplam Kullanım"
                                value={stats.totalUsage}
                                prefix={<i className="ti ti-users text-warning"></i>}
                                valueStyle={{ color: '#faad14', fontSize: '24px' }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Ek İstatistikler */}
                <Row gutter={16} className="mb-4">
                    <Col xs={24} sm={12}>
                        <Card className="text-center border-0 shadow-sm">
                            <Statistic
                                title="Toplam İndirim Verilen"
                                value={globalStats?.totalDiscountGiven || 0}
                                prefix="₺"
                                precision={2}
                                valueStyle={{ color: '#52c41a', fontSize: '20px' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Card className="text-center border-0 shadow-sm">
                            <Statistic
                                title="Ortalama İndirim"
                                value={globalStats?.averageDiscount || 0}
                                prefix="₺"
                                precision={2}
                                valueStyle={{ color: '#722ed1', fontSize: '20px' }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Filtreler */}
                <Card className="mb-4 border-0 shadow-sm">
                    <div className="row g-3">
                        <div className="col-md-4">
                            <Search
                                placeholder="Kupon kodu veya adı ara..."
                                allowClear
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                prefix={<SearchOutlined />}
                            />
                        </div>
                        <div className="col-md-2">
                            <Select
                                value={statusFilter}
                                onChange={setStatusFilter}
                                className="w-100"
                                placeholder="Durum"
                            >
                                <Option value="all">Tüm Durumlar</Option>
                                <Option value={CouponStatus.ACTIVE}>Aktif</Option>
                                <Option value={CouponStatus.INACTIVE}>Pasif</Option>
                                <Option value={CouponStatus.EXPIRED}>Süresi Dolmuş</Option>
                            </Select>
                        </div>
                        <div className="col-md-2">
                            <Select
                                value={typeFilter}
                                onChange={setTypeFilter}
                                className="w-100"
                                placeholder="Tip"
                            >
                                <Option value="all">Tüm Tipler</Option>
                                <Option value={CouponType.PERCENTAGE}>Yüzde</Option>
                                <Option value={CouponType.FIXED}>Sabit Tutar</Option>
                            </Select>
                        </div>
                        <div className="col-md-3">
                            <RangePicker
                                value={dateRange}
                                onChange={setDateRange}
                                placeholder={['Başlangıç', 'Bitiş']}
                                className="w-100"
                                format="DD.MM.YYYY"
                            />
                        </div>
                        <div className="col-md-1">
                            <Button
                                icon={<DownloadOutlined />}
                                className="w-100"
                                title="Excel'e Aktar"
                            />
                        </div>
                    </div>
                </Card>

                {/* Tablo */}
                <Card className="border-0 shadow-sm">
                    <Table
                        columns={columns}
                        dataSource={filteredCoupons}
                        rowKey="id"
                        loading={loading}
                        pagination={{
                            total: filteredCoupons.length,
                            pageSize: 10,
                            showTotal: (total, range) => 
                                `${range[0]}-${range[1]} / ${total} kupon`,
                        }}
                        scroll={{ x: 800 }}
                        className="custom-table"
                    />
                </Card>
            </div>

            <style>{`
                .custom-table .ant-table-thead > tr > th {
                    background: #f8f9fa;
                    border-bottom: 2px solid #e9ecef;
                    font-weight: 600;
                    color: #495057;
                }
                
                .custom-table .ant-table-tbody > tr:hover > td {
                    background: #f8f9fa;
                }
                
                .ant-card {
                    border-radius: 12px;
                }
                
                .ant-statistic-content {
                    font-weight: 600;
                }
            `}</style>
        </div>
    );
};

export default CouponsPage;
